import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Camera, CheckCircle2, Loader2, RotateCcw, Square, Video,
  Layers3, Box, Image as ImageIcon, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

/**
 * PitchMechanicsCapture — 2D + 3D markerless pitching analysis
 * ──────────────────────────────────────────────────────────────
 * Captures a ~5s clip of the athlete's mound delivery, runs MediaPipe Pose
 * Landmarker on every frame, and presents THREE simultaneous views the
 * coach/athlete can flip between:
 *
 *   A · 2D OVERLAY        — skeleton drawn on top of the recorded clip
 *                            (per-frame scrub)
 *   B · 3D WIREFRAME      — rotatable 3D point-cloud of MediaPipe world
 *                            landmarks (touch / mouse drag)
 *   C · 3D MOUND SCENE    — wireframe placed in a stylized 3D scene with
 *                            mound, rubber, and plate reference so the coach
 *                            can scrub through phases of the delivery.
 *
 * Camera convention (enforced by the wizard preamble):
 *   • RHP → camera on the 3rd-base side (sees the front of the pitcher)
 *   • LHP → camera on the 1st-base side
 *
 * Peak kinematics scraped from the clip (auto-suggested, coach-overridable):
 *   • Peak external rotation (deg)             — max shoulder ER at lay-back
 *   • Stride length % body height              — front-foot delta at FC
 *   • Trunk tilt at release (deg)              — forward lean from vertical
 *   • Front-knee flexion at FC + BR            — blocking-leg mechanics
 *   • Shoulder abduction at release            — arm-slot health (≈ 90°)
 *   • Hip-shoulder separation (deg)            — kinetic-chain stretch
 *
 * On-device inference (MediaPipe Tasks Vision · WASM + GPU). Video never
 * leaves the browser.
 */

const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";
const WASM_BASE_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

const LM = {
  NOSE: 0,
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_ELBOW: 13,    R_ELBOW: 14,
  L_WRIST: 15,    R_WRIST: 16,
  L_HIP: 23,      R_HIP: 24,
  L_KNEE: 25,     R_KNEE: 26,
  L_ANKLE: 27,    R_ANKLE: 28,
  L_HEEL: 29,     R_HEEL: 30,
  L_FOOT: 31,     R_FOOT: 32,
};

const SEGMENTS = [
  [LM.L_SHOULDER, LM.R_SHOULDER], [LM.L_SHOULDER, LM.L_HIP], [LM.R_SHOULDER, LM.R_HIP],
  [LM.L_HIP, LM.R_HIP], [LM.L_HIP, LM.L_KNEE], [LM.R_HIP, LM.R_KNEE],
  [LM.L_KNEE, LM.L_ANKLE], [LM.R_KNEE, LM.R_ANKLE],
  [LM.L_SHOULDER, LM.L_ELBOW], [LM.L_ELBOW, LM.L_WRIST],
  [LM.R_SHOULDER, LM.R_ELBOW], [LM.R_ELBOW, LM.R_WRIST],
];

// ─── Geometry helpers ─────────────────────────────────────────────────
const deg = (rad) => (rad * 180) / Math.PI;
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: ((a.z || 0) + (b.z || 0)) / 2 });

function angleDeg(a, b, c) {
  // 3D unsigned angle at vertex b
  const v1 = { x: a.x - b.x, y: a.y - b.y, z: (a.z || 0) - (b.z || 0) };
  const v2 = { x: c.x - b.x, y: c.y - b.y, z: (c.z || 0) - (b.z || 0) };
  const dot = v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
  const m1 = Math.hypot(v1.x, v1.y, v1.z);
  const m2 = Math.hypot(v2.x, v2.y, v2.z);
  if (!m1 || !m2) return null;
  return deg(Math.acos(Math.max(-1, Math.min(1, dot / (m1 * m2)))));
}

// Trunk forward lean from vertical (degrees). Positive = forward.
function trunkLeanDeg(shoulderMid, hipMid) {
  const dy = hipMid.y - shoulderMid.y;
  const dx = hipMid.x - shoulderMid.x;
  return Math.abs(deg(Math.atan2(dx, dy)));
}

// Hip-shoulder separation: angle between the pelvis line and the shoulder line,
// projected onto the horizontal (camera) plane. Returns degrees.
function hipShoulderSeparation(pose) {
  const ls = pose[LM.L_SHOULDER], rs = pose[LM.R_SHOULDER];
  const lh = pose[LM.L_HIP], rh = pose[LM.R_HIP];
  if (!ls || !rs || !lh || !rh) return null;
  const aShoulder = Math.atan2(rs.x - ls.x, rs.z - ls.z);
  const aHip = Math.atan2(rh.x - lh.x, rh.z - lh.z);
  return Math.abs(deg(aShoulder - aHip));
}

/**
 * Extract per-frame kinematics. Returns peak values + the frame index they
 * occurred at, so the 2D overlay can jump straight to the moment of interest.
 */
function analyzeClip(frames, handedness) {
  if (!frames.length) return { metrics: {}, peakFrames: {} };
  const throwShoulder = handedness === "right" ? LM.R_SHOULDER : LM.L_SHOULDER;
  const throwElbow = handedness === "right" ? LM.R_ELBOW : LM.L_ELBOW;
  const throwWrist = handedness === "right" ? LM.R_WRIST : LM.L_WRIST;
  const frontHip = handedness === "right" ? LM.L_HIP : LM.R_HIP;
  const frontKnee = handedness === "right" ? LM.L_KNEE : LM.R_KNEE;
  const frontAnkle = handedness === "right" ? LM.L_ANKLE : LM.R_ANKLE;
  const backAnkle = handedness === "right" ? LM.R_ANKLE : LM.L_ANKLE;

  let peakER = -Infinity, peakERFrame = 0;
  let peakStride = -Infinity, strideFrame = 0;
  let peakTrunk = -Infinity, trunkFrame = 0;
  let peakSep = -Infinity, sepFrame = 0;
  let peakAbd = -Infinity, abdFrame = 0;
  let peakHeadTilt = -Infinity, headTiltFrame = 0;
  let setHeadPos = null, peakHeadDrift = 0, headDriftFrame = 0;
  let kneeFlexBR = null, kneeFlexBRFrame = 0;
  let kneeFlexFC = null, kneeFlexFCFrame = 0;
  let releaseFrame = 0; // approximated as max wrist x-velocity

  let prevWristX = null;
  let prevWristTime = null;

  frames.forEach((f, i) => {
    const lm = f.world || f.image;
    if (!lm) return;
    const sh = lm[throwShoulder];
    const el = lm[throwElbow];
    const wr = lm[throwWrist];
    const hip = lm[frontHip];
    const kn = lm[frontKnee];
    const an = lm[frontAnkle];

    // Shoulder external rotation (angle wrist-elbow-shoulder)
    if (sh && el && wr) {
      const er = angleDeg(wr, el, sh);
      if (er != null && er > peakER) { peakER = er; peakERFrame = i; }
      // Shoulder abduction approximation (sh-elbow vs sh-hip axis)
      const abd = angleDeg(el, sh, lm[handedness === "right" ? LM.R_HIP : LM.L_HIP] || lm[LM.R_HIP]);
      if (abd != null && abd > peakAbd) { peakAbd = abd; abdFrame = i; }
    }

    // Stride length: distance between front ankle and back ankle, normalized to image height.
    if (lm[frontAnkle] && lm[backAnkle]) {
      const dx = Math.abs(lm[frontAnkle].x - lm[backAnkle].x);
      // Approx body height = nose Y to mean ankle Y, in image-space.
      const head = lm[LM.NOSE]?.y ?? 0;
      const ankleY = ((lm[frontAnkle].y || 0) + (lm[backAnkle].y || 0)) / 2;
      const bodyH = Math.max(0.001, Math.abs(ankleY - head));
      const pct = (dx / bodyH) * 100;
      if (pct > peakStride) { peakStride = pct; strideFrame = i; }
    }

    // Trunk lean at later frames (release window)
    const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER];
    const lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
    const nose = lm[LM.NOSE];
    if (ls && rs && lh && rh) {
      const sMid = mid(ls, rs), hMid = mid(lh, rh);
      const lean = trunkLeanDeg(sMid, hMid);
      if (lean > peakTrunk) { peakTrunk = lean; trunkFrame = i; }
      const sep = hipShoulderSeparation(lm);
      if (sep != null && sep > peakSep) { peakSep = sep; sepFrame = i; }

      // ─── Head capture (the 15° rule) ────────────────────────────
      // 1) Lateral tilt — angle of the head-to-shoulder-midpoint segment
      //    from vertical. Anything >15° = "dead weight" fighting throwing momentum.
      // 2) Lateral drift — how far the head has translated sideways from its
      //    "set position" (first frame the model could see the athlete).
      //    Every inch of inappropriate head movement costs distance at release.
      if (nose) {
        // Tilt: positive angle = head leaning sideways relative to torso vertical.
        // dx = nose.x − shoulder_mid.x, dy = shoulder_mid.y − nose.y (image coords)
        const dx = nose.x - sMid.x;
        const dy = Math.max(0.001, sMid.y - nose.y);  // shoulders below nose in image-space
        const tiltDeg = Math.abs(deg(Math.atan2(dx, dy)));
        if (tiltDeg > peakHeadTilt) { peakHeadTilt = tiltDeg; headTiltFrame = i; }

        // Drift: x-distance from the "set position" (first sane frame).
        if (setHeadPos == null) setHeadPos = { x: nose.x, y: nose.y };
        const drift = Math.abs(nose.x - setHeadPos.x);
        if (drift > peakHeadDrift) { peakHeadDrift = drift; headDriftFrame = i; }
      }
    }

    // Front knee flexion at FC (peak stride frame, reused below)
    if (hip && kn && an) {
      const flex = angleDeg(hip, kn, an);
      if (i === strideFrame && flex != null) { kneeFlexFC = 180 - flex; kneeFlexFCFrame = i; }
    }

    // Wrist x-velocity to find release frame
    if (wr) {
      if (prevWristX != null) {
        const dxv = Math.abs(wr.x - prevWristX);
        const dt = (f.t - prevWristTime) || 1;
        const v = dxv / dt;
        if (v > (frames._maxVel || 0)) { frames._maxVel = v; releaseFrame = i; }
      }
      prevWristX = wr.x; prevWristTime = f.t;
    }
  });

  // Knee flex at ball release (use release frame)
  const rlm = (frames[releaseFrame]?.world || frames[releaseFrame]?.image);
  if (rlm) {
    const flex = angleDeg(rlm[frontHip], rlm[frontKnee], rlm[frontAnkle]);
    if (flex != null) { kneeFlexBR = 180 - flex; kneeFlexBRFrame = releaseFrame; }
  }

  return {
    peakFrames: {
      ext_rotation: peakERFrame,
      stride: strideFrame,
      trunk: trunkFrame,
      separation: sepFrame,
      abduction: abdFrame,
      release: releaseFrame,
      knee_fc: kneeFlexFCFrame,
      knee_br: kneeFlexBRFrame,
      head_tilt: headTiltFrame,
      head_drift: headDriftFrame,
    },
    metrics: {
      peak_ext_rotation_deg: peakER > 0 ? +peakER.toFixed(1) : null,
      stride_length_pct: peakStride > 0 ? +peakStride.toFixed(1) : null,
      trunk_tilt_release_deg: peakTrunk > 0 ? +peakTrunk.toFixed(1) : null,
      front_knee_flex_fc_deg: kneeFlexFC != null ? +kneeFlexFC.toFixed(1) : null,
      front_knee_flex_br_deg: kneeFlexBR != null ? +kneeFlexBR.toFixed(1) : null,
      shoulder_abduction_deg: peakAbd > 0 ? +peakAbd.toFixed(1) : null,
      hip_shoulder_separation_deg: peakSep > 0 ? +peakSep.toFixed(1) : null,
      // ── Head capture ────────────────────────────────────────────
      head_tilt_max_deg: peakHeadTilt > 0 ? +peakHeadTilt.toFixed(1) : null,
      // Drift is a normalized-x delta. Convert to inches assuming a 5.5-ft camera
      // crop height (typical) and the athlete fills ~80% of the frame height —
      // body height ≈ 5.5 ft × 0.8 = 4.4 ft → 52.8 in across the visible frame
      // width. So 1.0 unit drift ≈ 52.8 in. This is a rough proxy; the coach
      // can override.
      head_drift_in: peakHeadDrift > 0 ? +(peakHeadDrift * 52.8).toFixed(1) : null,
      head_drift_norm: peakHeadDrift > 0 ? +peakHeadDrift.toFixed(3) : null,
    },
  };
}

// ─── 3D projection (no Three.js — keep dep footprint zero) ─────────────
function project3D(p, rotX, rotY, scale, cx, cy) {
  // Rotate around Y then X.
  let { x, y, z } = p;
  const cosY = Math.cos(rotY), sinY = Math.sin(rotY);
  const cosX = Math.cos(rotX), sinX = Math.sin(rotX);
  let x1 = x * cosY + z * sinY;
  let z1 = -x * sinY + z * cosY;
  let y1 = y * cosX - z1 * sinX;
  let z2 = y * sinX + z1 * cosX;
  // Perspective
  const depth = 2.5;
  const k = depth / (depth + z2);
  return { x: cx + x1 * scale * k, y: cy + y1 * scale * k, z: z2 };
}

function drawWireframe(ctx, w, h, landmarks, rotX, rotY, opts = {}) {
  if (!landmarks) return;
  ctx.clearRect(0, 0, w, h);
  if (opts.background) {
    ctx.fillStyle = opts.background;
    ctx.fillRect(0, 0, w, h);
  }
  const cx = w / 2, cy = h / 2;
  const scale = Math.min(w, h) * 0.45;
  // Mound + rubber + plate reference if scene mode
  if (opts.scene) {
    drawMoundScene(ctx, w, h, rotX, rotY, scale, cx, cy);
  }
  // Project all landmarks
  const pts = landmarks.map((p) => project3D({ x: p.x, y: -p.y, z: -(p.z || 0) }, rotX, rotY, scale, cx, cy));
  // Draw segments
  ctx.lineWidth = opts.scene ? 4 : 5;
  ctx.lineCap = "round";
  for (const [a, b] of SEGMENTS) {
    const pa = pts[a], pb = pts[b];
    if (!pa || !pb) continue;
    const avgZ = (pa.z + pb.z) / 2;
    const alpha = Math.max(0.35, 1 - avgZ * 0.4);
    ctx.strokeStyle = `rgba(34,211,238,${alpha})`;
    ctx.beginPath(); ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y); ctx.stroke();
  }
  // Joints
  const keyJoints = [LM.L_SHOULDER, LM.R_SHOULDER, LM.L_HIP, LM.R_HIP,
                     LM.L_KNEE, LM.R_KNEE, LM.L_ANKLE, LM.R_ANKLE,
                     LM.L_ELBOW, LM.R_ELBOW, LM.L_WRIST, LM.R_WRIST];
  for (const i of keyJoints) {
    const p = pts[i]; if (!p) continue;
    ctx.fillStyle = "rgba(217,4,41,0.95)";
    ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2); ctx.fill();
  }
}

function drawMoundScene(ctx, w, h, rotX, rotY, scale, cx, cy) {
  // Mound ellipse + rubber + plate, projected with same camera.
  const ring = [];
  for (let a = 0; a < Math.PI * 2; a += Math.PI / 24) {
    ring.push({ x: Math.cos(a) * 0.7, y: -0.55, z: Math.sin(a) * 0.5 });
  }
  ctx.beginPath();
  ring.forEach((p, i) => {
    const q = project3D(p, rotX, rotY, scale, cx, cy);
    if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y);
  });
  ctx.closePath();
  ctx.fillStyle = "rgba(217,4,41,0.08)";
  ctx.strokeStyle = "rgba(217,4,41,0.5)";
  ctx.lineWidth = 1.5;
  ctx.fill(); ctx.stroke();

  // Pitching rubber (white slab) on top of the mound.
  const rubber = [
    { x: -0.25, y: -0.51, z: -0.04 },
    { x:  0.25, y: -0.51, z: -0.04 },
    { x:  0.25, y: -0.51, z:  0.04 },
    { x: -0.25, y: -0.51, z:  0.04 },
  ];
  ctx.beginPath();
  rubber.forEach((p, i) => {
    const q = project3D(p, rotX, rotY, scale, cx, cy);
    if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y);
  });
  ctx.closePath();
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fill();

  // Home plate (white pentagon) downrange.
  const plate = [
    { x: -0.17, y: -0.55, z: 1.6 },
    { x:  0.17, y: -0.55, z: 1.6 },
    { x:  0.17, y: -0.55, z: 1.85 },
    { x:  0,    y: -0.55, z: 2.0 },
    { x: -0.17, y: -0.55, z: 1.85 },
  ];
  ctx.beginPath();
  plate.forEach((p, i) => {
    const q = project3D(p, rotX, rotY, scale, cx, cy);
    if (i === 0) ctx.moveTo(q.x, q.y); else ctx.lineTo(q.x, q.y);
  });
  ctx.closePath();
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.strokeStyle = "rgba(34,211,238,0.6)";
  ctx.fill(); ctx.stroke();

  // Distance label
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.font = "bold 10px ui-sans-serif, system-ui";
  ctx.textAlign = "center";
  const midPoint = project3D({ x: 0, y: -0.55, z: 0.9 }, rotX, rotY, scale, cx, cy);
  ctx.fillText("60' 6\" → plate", midPoint.x, midPoint.y - 4);
}

// ─── Main component ───────────────────────────────────────────────────
export default function PitchMechanicsCapture({
  handedness = "right",
  recordSec = 6,
  onComplete,
  onCancel,
}) {
  const videoRef = useRef(null);
  const canvas2DRef = useRef(null);  // live overlay
  const wireRef = useRef(null);      // wireframe view
  const sceneRef = useRef(null);     // 3D mound scene
  const rafRef = useRef(null);
  const landmarkerRef = useRef(null);
  const streamRef = useRef(null);
  const captureRef = useRef([]);     // [{ t, image: [...], world: [...] }]
  const timerRef = useRef(null);

  const [permission, setPermission] = useState("pending");
  const [phase, setPhase] = useState("setup"); // setup | loading | ready | recording | analyzing | done
  const [secondsLeft, setSecondsLeft] = useState(recordSec);
  const [conf, setConf] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [viewMode, setViewMode] = useState("2d"); // 2d | wire | scene
  const [scrubFrame, setScrubFrame] = useState(0);
  const [rot, setRot] = useState({ x: 0.05, y: 0.4 });
  const dragRef = useRef(null);

  const cameraSide = handedness === "right" ? "3rd_base" : "1st_base";
  const cameraLabel = handedness === "right" ? "3rd-base side" : "1st-base side";

  const initModel = async () => {
    setPhase("loading");
    try {
      const vision = await import("@mediapipe/tasks-vision");
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM_BASE_URL);
      landmarkerRef.current = await vision.PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      setPhase("ready");
    } catch (e) {
      console.error("MediaPipe init failed", e);
      toast.error("Pose detector failed to load — try again or skip.");
      setPhase("setup");
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermission("unsupported");
      toast.error("This browser doesn't expose a camera API.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setPermission("granted");
      if (!landmarkerRef.current) await initModel();
    } catch (e) {
      console.error(e);
      setPermission("denied");
      toast.error("Camera permission denied.");
    }
  };

  // Live overlay loop (during ready & recording)
  useEffect(() => {
    if (phase !== "ready" && phase !== "recording") return;
    const v = videoRef.current, c = canvas2DRef.current;
    if (!v || !c || !landmarkerRef.current) return;
    const ctx = c.getContext("2d");
    const loop = () => {
      if (!videoRef.current || !landmarkerRef.current) return;
      try {
        const t = performance.now();
        const res = landmarkerRef.current.detectForVideo(v, t);
        c.width = v.videoWidth || 720;
        c.height = v.videoHeight || 1280;
        ctx.drawImage(v, 0, 0, c.width, c.height);
        const image = res?.landmarks?.[0];
        const world = res?.worldLandmarks?.[0];
        if (image) {
          const visConf = Math.min(...[LM.L_SHOULDER, LM.R_SHOULDER, LM.L_HIP, LM.R_HIP]
                                      .map(i => image[i]?.visibility ?? 1));
          setConf(visConf);
          drawOverlay(ctx, c.width, c.height, image, handedness);
          if (phase === "recording") {
            captureRef.current.push({ t, image, world });
          }
        } else {
          setConf(0);
        }
      } catch { /* noop */ }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [phase, handedness]);

  // Post-record: when scrubFrame or viewMode changes, redraw
  useEffect(() => {
    if (phase !== "done") return;
    const frames = captureRef.current;
    const frame = frames[scrubFrame];
    if (!frame) return;
    // 2D view — paint a "freeze" of the last camera frame is not stored; instead just draw skeleton on black
    const c2 = canvas2DRef.current;
    if (c2 && viewMode === "2d") {
      const ctx = c2.getContext("2d");
      c2.width = 720; c2.height = 1280;
      ctx.fillStyle = "rgba(5,11,22,1)";
      ctx.fillRect(0, 0, c2.width, c2.height);
      drawOverlay(ctx, c2.width, c2.height, frame.image, handedness, {
        annotateMetric: peakMetricLabelForFrame(scrubFrame, analysis),
      });
    }
    // 3D Wireframe
    const wc = wireRef.current;
    if (wc && (viewMode === "wire" || viewMode === "scene")) {
      const wctx = wc.getContext("2d");
      const W = wc.clientWidth || 360, H = wc.clientHeight || 480;
      wc.width = W; wc.height = H;
      drawWireframe(wctx, W, H, frame.world, rot.x, rot.y,
        { background: viewMode === "scene" ? "rgba(5,11,22,0.65)" : "rgba(5,11,22,0.45)", scene: viewMode === "scene" });
    }
    const sc = sceneRef.current;
    if (sc && viewMode === "scene") {
      const sctx = sc.getContext("2d");
      const W = sc.clientWidth || 360, H = sc.clientHeight || 480;
      sc.width = W; sc.height = H;
      drawWireframe(sctx, W, H, frame.world, rot.x, rot.y,
        { background: "rgba(5,11,22,0.65)", scene: true });
    }
  }, [scrubFrame, viewMode, rot, analysis, phase, handedness]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const startRecording = () => {
    captureRef.current = [];
    setSecondsLeft(recordSec);
    setPhase("recording");
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          finishRecording();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const finishRecording = () => {
    setPhase("analyzing");
    // Stop camera ASAP so the phone can free GPU for analysis.
    streamRef.current?.getTracks().forEach((t) => t.stop());
    // Defer analysis to next tick so UI updates
    setTimeout(() => {
      const result = analyzeClip(captureRef.current, handedness);
      const avgVis = captureRef.current.length
        ? captureRef.current.reduce((acc, f) => {
            const lm = f.image || [];
            const cs = [LM.L_SHOULDER, LM.R_SHOULDER, LM.L_HIP, LM.R_HIP]
              .map(i => lm[i]?.visibility ?? 0);
            return acc + cs.reduce((a, b) => a + b, 0) / cs.length;
          }, 0) / captureRef.current.length
        : 0;
      setAnalysis({ ...result, confidence: +avgVis.toFixed(2) });
      setScrubFrame(result.peakFrames.release || 0);
      setPhase("done");
    }, 100);
  };

  const stopEarly = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    finishRecording();
  };

  const reset = () => {
    captureRef.current = [];
    setAnalysis(null);
    setScrubFrame(0);
    setPhase("setup");
    setPermission("pending");
  };

  const submit = () => {
    if (!analysis) return;
    onComplete?.({
      handedness,
      camera_side: cameraSide,
      ...analysis.metrics,
      confidence: analysis.confidence,
      frame_count: captureRef.current.length,
    });
  };

  // ─── Pointer-drag rotate for 3D views ────────────────────────────
  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, rx: rot.x, ry: rot.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    setRot({ x: dragRef.current.rx + dy * 0.01, y: dragRef.current.ry + dx * 0.01 });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const peakFrames = analysis?.peakFrames || {};
  const peakChips = useMemo(() => ([
    { key: "ext_rotation", label: "Peak ER", metric: "peak_ext_rotation_deg", unit: "°" },
    { key: "stride",       label: "Stride", metric: "stride_length_pct", unit: "%" },
    { key: "trunk",        label: "Trunk lean", metric: "trunk_tilt_release_deg", unit: "°" },
    { key: "separation",   label: "Hip-shoulder sep", metric: "hip_shoulder_separation_deg", unit: "°" },
    { key: "release",      label: "Release", metric: null },
    { key: "knee_br",      label: "Knee @ BR", metric: "front_knee_flex_br_deg", unit: "°" },
    { key: "head_tilt",    label: "Head tilt", metric: "head_tilt_max_deg", unit: "°" },
    { key: "head_drift",   label: "Head drift", metric: "head_drift_in", unit: "\"" },
  ]), []);

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid="pitch-mechanics-capture">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">2D + 3D Markerless</div>
          <div className="text-lg font-display font-black text-white mt-1">Pitch Mechanics Capture</div>
        </div>
        <Video className="h-5 w-5 text-white/40" />
      </div>

      {/* Camera-side preamble */}
      <div className="rounded-lg p-3 mb-3 border border-cyan-500/30 bg-cyan-500/5 text-xs text-cyan-100">
        <div className="font-bold text-cyan-200 mb-0.5">
          Camera position · {cameraLabel} ({handedness === "right" ? "RHP" : "LHP"})
        </div>
        Mount the phone on a tripod ~15-20 ft from the mound on the
        <b className="text-white"> {cameraLabel}</b>. Keep the lens at hip height,
        framing the whole body. Athlete delivers one pitch toward home plate.
      </div>

      {/* LIVE / CAPTURED 2D + 3D VIEW WALL */}
      {phase !== "done" ? (
        <div className="relative rounded-xl overflow-hidden bg-black border border-white/10 mb-3"
             style={{ aspectRatio: "9 / 16", maxHeight: 520 }}>
          <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover opacity-0" muted playsInline />
          <canvas ref={canvas2DRef} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] font-bold text-white">
            <span className="bg-black/60 backdrop-blur-md px-2.5 py-1.5 rounded">
              {phase === "setup" && "Camera not started"}
              {phase === "loading" && "Loading pose detector…"}
              {phase === "ready" && "Position the pitcher in frame"}
              {phase === "recording" && `Recording · ${secondsLeft}s`}
              {phase === "analyzing" && "Analyzing…"}
            </span>
            {(phase === "ready" || phase === "recording") && (
              <span className="bg-black/60 backdrop-blur-md px-2.5 py-1.5 rounded">Conf {(conf * 100).toFixed(0)}%</span>
            )}
          </div>
          {phase === "loading" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading on-device pose model…
            </div>
          )}
          {phase === "analyzing" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white text-sm">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Extracting kinematics…
            </div>
          )}
        </div>
      ) : (
        // ── DONE: triple-view review ─────────────────────────────────
        <div className="space-y-3">
          {/* View-mode tabs */}
          <div className="flex gap-2" data-testid="pitch-view-tabs">
            {[
              { v: "2d",    icon: ImageIcon, label: "2D Overlay" },
              { v: "wire",  icon: Box,       label: "3D Wireframe" },
              { v: "scene", icon: Layers3,   label: "3D Mound" },
            ].map(({ v, icon: Icon, label }) => (
              <button key={v} onClick={() => setViewMode(v)} data-testid={`pitch-view-${v}`}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider
                            ${viewMode === v
                              ? "bg-[var(--brand-secondary)]/15 border border-[var(--brand-secondary)]/60 text-white"
                              : "bg-black/30 border border-white/10 text-white/60 hover:text-white"}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          {/* View pane */}
          <div className="relative rounded-xl overflow-hidden bg-black border border-white/10"
               style={{ aspectRatio: "9 / 16", maxHeight: 520 }}
               onPointerDown={onPointerDown}
               onPointerMove={onPointerMove}
               onPointerUp={onPointerUp}
               onPointerLeave={onPointerUp}>
            {viewMode === "2d" && (
              <canvas ref={canvas2DRef} className="absolute inset-0 w-full h-full object-cover" />
            )}
            {(viewMode === "wire" || viewMode === "scene") && (
              <canvas ref={viewMode === "wire" ? wireRef : sceneRef}
                      className="absolute inset-0 w-full h-full object-cover cursor-grab active:cursor-grabbing"
                      data-testid={`pitch-canvas-${viewMode}`} />
            )}
            <div className="absolute top-3 left-3 bg-black/60 px-2 py-1 rounded text-[10px] uppercase tracking-[0.18em] font-bold text-white">
              Frame {scrubFrame + 1} / {captureRef.current.length}
            </div>
            {viewMode !== "2d" && (
              <div className="absolute bottom-3 left-3 right-3 text-center bg-black/60 px-2 py-1 rounded text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">
                Drag to rotate · 3D world landmarks
              </div>
            )}
          </div>

          {/* Phase scrubber + peak quick-jumps */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <button onClick={() => setScrubFrame(Math.max(0, scrubFrame - 1))}
                      className="p-1.5 rounded bg-black/30 text-white/70 hover:text-white" data-testid="pitch-scrub-prev">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input type="range" min={0} max={Math.max(0, captureRef.current.length - 1)}
                     value={scrubFrame} onChange={(e) => setScrubFrame(+e.target.value)}
                     className="flex-1" data-testid="pitch-scrubber" />
              <button onClick={() => setScrubFrame(Math.min(captureRef.current.length - 1, scrubFrame + 1))}
                      className="p-1.5 rounded bg-black/30 text-white/70 hover:text-white" data-testid="pitch-scrub-next">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {peakChips.map((c) => {
                const v = c.metric ? analysis?.metrics?.[c.metric] : null;
                const fi = peakFrames[c.key];
                if (fi == null) return null;
                return (
                  <button key={c.key} onClick={() => setScrubFrame(fi)}
                          data-testid={`pitch-peak-${c.key}`}
                          className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold border
                                      ${scrubFrame === fi
                                        ? "border-[var(--brand-secondary)]/70 bg-[var(--brand-secondary)]/15 text-white"
                                        : "border-white/15 bg-black/30 text-white/65 hover:text-white"}`}>
                    {c.label}{v != null ? ` · ${v}${c.unit || ""}` : ""}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Metrics card */}
          <div className="rounded-lg p-4 border border-emerald-500/30 bg-emerald-500/5" data-testid="pitch-metrics">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-2">Auto-extracted kinematics</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(analysis?.metrics || {}).map(([k, v]) => (
                <div key={k} className="rounded p-2 bg-black/30 border border-white/5">
                  <div className="text-[10px] text-white/55 uppercase tracking-wider">{k.replace(/_/g, " ")}</div>
                  <div className="text-white font-display font-bold text-base mt-0.5">{v == null ? "—" : v}{labelUnit(k)}</div>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-white/40 mt-2">Capture confidence: {Math.round((analysis?.confidence || 0) * 100)}% · Coach can override before saving.</div>
          </div>

          {/* THE 15° RULE — head capture coaching callout */}
          <HeadCaptureCallout metrics={analysis?.metrics} onJump={(fi) => setScrubFrame(fi)} peakFrames={analysis?.peakFrames} />

          <div className="flex gap-2">
            <Button onClick={reset} variant="outline" className="flex-1 border-white/20 text-white" data-testid="pitch-retake">
              <RotateCcw className="h-4 w-4 mr-2" /> Re-record
            </Button>
            <Button onClick={submit} className="flex-1" style={{ backgroundColor: "var(--brand-secondary)" }}
                    data-testid="pitch-save">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Save mechanics
            </Button>
          </div>
        </div>
      )}

      {/* Controls (setup → ready → recording) */}
      {phase !== "done" && (
        <>
          {permission === "pending" && phase === "setup" && (
            <Button onClick={startCamera} className="w-full" data-testid="pitch-grant-camera">
              <Camera className="h-4 w-4 mr-2" /> Enable Camera
            </Button>
          )}
          {phase === "ready" && (
            <Button onClick={startRecording} className="w-full" style={{ backgroundColor: "var(--brand-secondary)" }}
                    data-testid="pitch-start-record">
              ▶ Record {recordSec}s clip
            </Button>
          )}
          {phase === "recording" && (
            <Button onClick={stopEarly} variant="destructive" className="w-full" data-testid="pitch-stop-record">
              <Square className="h-4 w-4 mr-2" /> Stop early ({secondsLeft}s left)
            </Button>
          )}
        </>
      )}

      <button onClick={onCancel} className="mt-4 text-xs text-white/40 hover:text-white/70 w-full" data-testid="pitch-cancel">
        Skip pitch-mechanics capture
      </button>

      <div className="mt-3 text-[10px] text-white/30 text-center">
        On-device inference · No video leaves your phone · MediaPipe pose
      </div>
    </Card>
  );
}

// ─── Render helpers ───────────────────────────────────────────────────
function drawOverlay(ctx, w, h, pose, handedness, opts = {}) {
  // Skeleton (cyan) + throwing-arm chain highlight (red) + key annotations.
  const throwShoulder = handedness === "right" ? LM.R_SHOULDER : LM.L_SHOULDER;
  const throwElbow = handedness === "right" ? LM.R_ELBOW : LM.L_ELBOW;
  const throwWrist = handedness === "right" ? LM.R_WRIST : LM.L_WRIST;
  const px = (lm) => ({ x: lm.x * w, y: lm.y * h });

  ctx.lineWidth = 5; ctx.lineCap = "round";
  ctx.strokeStyle = "rgba(34,211,238,0.9)";
  for (const [a, b] of SEGMENTS) {
    const pa = pose[a], pb = pose[b];
    if (!pa || !pb) continue;
    ctx.beginPath(); ctx.moveTo(pa.x * w, pa.y * h); ctx.lineTo(pb.x * w, pb.y * h); ctx.stroke();
  }
  // Throwing-arm chain over the top
  ctx.strokeStyle = "rgba(217,4,41,0.95)"; ctx.lineWidth = 6;
  [[throwShoulder, throwElbow], [throwElbow, throwWrist]].forEach(([a, b]) => {
    const pa = pose[a], pb = pose[b];
    if (!pa || !pb) return;
    ctx.beginPath(); ctx.moveTo(pa.x * w, pa.y * h); ctx.lineTo(pb.x * w, pb.y * h); ctx.stroke();
  });
  for (const i of [LM.L_HIP, LM.R_HIP, LM.L_KNEE, LM.R_KNEE, LM.L_SHOULDER, LM.R_SHOULDER, throwElbow, throwWrist]) {
    const p = pose[i]; if (!p) continue;
    ctx.fillStyle = "rgba(217,4,41,0.95)";
    ctx.beginPath(); ctx.arc(p.x * w, p.y * h, 6, 0, Math.PI * 2); ctx.fill();
  }
  // Annotation
  if (opts.annotateMetric) {
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillRect(12, h - 48, 280, 36);
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.font = "bold 13px ui-sans-serif, system-ui";
    ctx.textAlign = "left";
    ctx.fillText(opts.annotateMetric, 22, h - 24);
  }
}

function peakMetricLabelForFrame(frameIdx, analysis) {
  if (!analysis) return null;
  const map = {
    ext_rotation: ["Peak external rotation", "peak_ext_rotation_deg", "°"],
    stride:       ["Stride length", "stride_length_pct", "%"],
    trunk:        ["Trunk lean", "trunk_tilt_release_deg", "°"],
    separation:   ["Hip-shoulder separation", "hip_shoulder_separation_deg", "°"],
    release:      ["Ball release", null, ""],
    knee_br:      ["Front-knee flex (BR)", "front_knee_flex_br_deg", "°"],
    knee_fc:      ["Front-knee flex (FC)", "front_knee_flex_fc_deg", "°"],
    abduction:    ["Shoulder abduction", "shoulder_abduction_deg", "°"],
    head_tilt:    ["Peak head tilt", "head_tilt_max_deg", "°"],
    head_drift:   ["Peak head drift", "head_drift_in", "\""],
  };
  for (const [k, fi] of Object.entries(analysis.peakFrames)) {
    if (fi === frameIdx && map[k]) {
      const [label, mkey, unit] = map[k];
      const v = mkey ? analysis.metrics[mkey] : null;
      return v != null ? `${label}: ${v}${unit}` : label;
    }
  }
  return null;
}

function labelUnit(metric) {
  if (metric.endsWith("_pct")) return "%";
  if (metric.endsWith("_deg")) return "°";
  if (metric.endsWith("_in")) return "\"";
  if (metric === "head_drift_norm") return "";
  return "";
}

/**
 * HeadCaptureCallout — surfaces the 15-degree rule.
 *   • Stable head from set position to release with <15° lateral tilt.
 *   • Excess movement = "dead weight" fighting throwing momentum.
 *   • Every inch of head drift can cost distance at release → lost real and
 *     perceived velocity.
 *   • Correct head posture (eyes level, chin tucked) → stable spinal
 *     alignment → efficient energy transfer through the trunk.
 */
function HeadCaptureCallout({ metrics, peakFrames, onJump }) {
  if (!metrics) return null;
  const tilt = metrics.head_tilt_max_deg;
  const drift = metrics.head_drift_in;
  if (tilt == null && drift == null) return null;

  const tiltOver = tilt != null && tilt > 15;
  const driftHigh = drift != null && drift > 3.0;   // >3 in considered significant drift
  const status = tiltOver || driftHigh ? "fail" : "pass";

  return (
    <div className={`rounded-lg p-4 border ${
      status === "fail"
        ? "border-amber-500/40 bg-amber-500/5"
        : "border-emerald-500/30 bg-emerald-500/5"
    }`} data-testid="pitch-head-capture">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white">
          Head Capture · the 15° rule
        </div>
        <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${
          status === "fail" ? "text-amber-300" : "text-emerald-300"
        }`} data-testid="head-capture-status">
          {status === "fail" ? "Concern" : "Stable"}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs mb-2">
        <button onClick={() => onJump?.(peakFrames?.head_tilt ?? 0)}
                className="rounded p-2 bg-black/30 border border-white/5 hover:border-white/20 text-left"
                data-testid="head-tilt-chip">
          <div className="text-[10px] text-white/55 uppercase tracking-wider">Peak lateral tilt</div>
          <div className={`font-display font-bold text-base mt-0.5 ${tiltOver ? "text-amber-300" : "text-white"}`}>
            {tilt == null ? "—" : `${tilt}°`}
            <span className="text-[10px] text-white/40 ml-1">/ 15° limit</span>
          </div>
        </button>
        <button onClick={() => onJump?.(peakFrames?.head_drift ?? 0)}
                className="rounded p-2 bg-black/30 border border-white/5 hover:border-white/20 text-left"
                data-testid="head-drift-chip">
          <div className="text-[10px] text-white/55 uppercase tracking-wider">Lateral head drift</div>
          <div className={`font-display font-bold text-base mt-0.5 ${driftHigh ? "text-amber-300" : "text-white"}`}>
            {drift == null ? "—" : `${drift}″`}
          </div>
        </button>
      </div>
      <div className="text-[11px] text-white/65 leading-relaxed">
        {status === "fail" ? (
          <>
            <b className="text-amber-200">Excess head movement</b> shifts the center of gravity and adds dead-weight resistance to throwing momentum. Every inch of inappropriate drift can cost <b className="text-white">real &amp; perceived velocity</b> at release. Drill set: chin-tucked T-spine extension, eyes-level mirror drills, and tempo holds in the lay-back position.
          </>
        ) : (
          <>
            Head stays stable from set position to release with tilt under the 15° threshold — strong spinal alignment for efficient trunk energy transfer.
          </>
        )}
      </div>
    </div>
  );
}
